
import { useState, useEffect, KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetFooter, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function LoginForm() {
  const { login } = useAuth();
  const { toast } = useToast();
  const [inputUsername, setInputUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isLocked, setIsLocked] = useState(false);

  // Effect to handle login lockout countdown
  useEffect(() => {
    // Check if there's stored lockout data
    const storedLockout = localStorage.getItem('login_lockout');
    if (storedLockout) {
      const lockoutData = JSON.parse(storedLockout);
      const now = Date.now();
      
      if (lockoutData.until > now) {
        setLockoutTime(lockoutData.until);
        setLoginAttempts(lockoutData.attempts);
        setIsLocked(true);
      } else {
        // Lockout period expired
        localStorage.removeItem('login_lockout');
      }
    }

    // Check for stored attempts if not locked
    if (!isLocked) {
      const storedAttempts = localStorage.getItem('login_attempts');
      if (storedAttempts) {
        setLoginAttempts(parseInt(storedAttempts, 10));
      }
    }
  }, []);

  // Effect for countdown timer
  useEffect(() => {
    let interval: number;
    
    if (isLocked && lockoutTime) {
      const updateRemaining = () => {
        const remaining = Math.max(0, Math.floor((lockoutTime - Date.now()) / 1000));
        setTimeRemaining(remaining);
        
        if (remaining <= 0) {
          clearInterval(interval);
          setIsLocked(false);
          setLoginAttempts(0);
          localStorage.removeItem('login_lockout');
          localStorage.removeItem('login_attempts');
          toast({
            title: "Η προσωρινή φραγή έληξε",
            description: "Μπορείτε να προσπαθήσετε ξανά να συνδεθείτε",
          });
        }
      };
      
      updateRemaining();
      interval = window.setInterval(updateRemaining, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLocked, lockoutTime, toast]);

  const handleLogin = () => {
    // If account is locked, don't proceed
    if (isLocked) return;

    // For demo users, bypass attempt tracking
    if ((inputUsername === "admin" && password === "admin") || 
        (inputUsername === "demo" && password === "demo")) {
      login(inputUsername, password);
      return;
    }

    // Check signup data or proceed with attempt tracking
    const signupData = localStorage.getItem('signupData');
    if (signupData) {
      const userData = JSON.parse(signupData);
      if (userData.username === inputUsername && userData.password === password) {
        login(inputUsername, password);
        // Reset attempts on successful login
        setLoginAttempts(0);
        localStorage.removeItem('login_attempts');
        return;
      }
    }

    // Failed login attempt
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    localStorage.setItem('login_attempts', newAttempts.toString());
    
    // Check if we should lock the account
    if (newAttempts >= 3) {
      const lockoutUntil = Date.now() + (3 * 60 * 1000); // 3 minutes
      setLockoutTime(lockoutUntil);
      setIsLocked(true);
      localStorage.setItem('login_lockout', JSON.stringify({
        attempts: newAttempts,
        until: lockoutUntil
      }));
      
      toast({
        title: "Προσωρινό κλείδωμα λογαριασμού",
        description: "Έχετε κάνει πολλές αποτυχημένες προσπάθειες. Παρακαλώ δοκιμάστε ξανά σε 3 λεπτά.",
        variant: "destructive",
      });
    } else {
      // Normal error toast for failed login
      toast({
        title: "Σφάλμα",
        description: "Λάθος στοιχεία σύνδεσης",
        variant: "destructive"
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLocked) {
      handleLogin();
    }
  };

  // Format time remaining (mm:ss)
  const formatTimeRemaining = () => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline">Σύνδεση</Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Σύνδεση</SheetTitle>
          <SheetDescription>
            Συνδεθείτε για να αποκτήσετε πρόσβαση στο εκπαιδευτικό υλικό
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          {isLocked && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                Πολλές αποτυχημένες προσπάθειες. Δοκιμάστε ξανά σε {formatTimeRemaining()}.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <label htmlFor="username" className="text-sm font-medium">Username</label>
            <Input 
              id="username" 
              value={inputUsername} 
              onChange={(e) => setInputUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLocked}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">Password</label>
            <Input 
              id="password" 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLocked}
            />
          </div>
          <SheetFooter>
            <Button 
              className="w-full" 
              onClick={handleLogin} 
              disabled={isLocked}
            >
              Σύνδεση
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
